import { useCallback, useState } from 'react';
import { captureSupabaseError } from '../lib/supabaseError';

import { axiosRubyClient } from '@/util/axios/axiosClient';

import type { AudienceFilter } from '../types';

interface CreateBroadcastParams {
  content: string;
  title: string;
  expireAt: string;
  audienceFilter: AudienceFilter;
}

interface CreateBroadcastResponse {
  success: boolean;
  data: {
    broadcast_id: string;
    recipient_count: number;
  };
  errors?: string[];
}

export function useCreateBroadcast() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async ({
      content,
      title,
      expireAt,
      audienceFilter,
    }: CreateBroadcastParams) => {
      const trimmed = content.trim();
      if (!trimmed) throw new Error('Broadcast content is empty');
      const trimmedTitle = title.trim();
      if (!trimmedTitle) throw new Error('Broadcast title is empty');
      if (!expireAt) throw new Error('Broadcast expiry is empty');

      setCreating(true);
      setError(null);
      try {
        const res = await axiosRubyClient.post<CreateBroadcastResponse>(
          '/api/admin/broadcasts',
          {
            content: trimmed,
            title: trimmedTitle,
            expireAt,
            message_type: 'broadcast',
            audience_filter: audienceFilter,
          },
        );
        return res.data.data;
      } catch (err: unknown) {
        const message =
          (
            err as { response?: { data?: { errors?: string[] } } }
          )?.response?.data?.errors?.join(', ') ??
          (err as Error)?.message ??
          'Failed to create broadcast';
        captureSupabaseError(err, { action: 'create_broadcast' });
        setError(message);
        throw err;
      } finally {
        setCreating(false);
      }
    },
    [],
  );

  return { create, creating, error };
}
